/**
 * Centralized API Client with Response Normalization
 * 
 * IMPORTANT: This client MAINTAINS snake_case throughout per architectural decision.
 * We do NOT convert between snake_case and camelCase - this eliminates conversion bugs
 * and maintains consistency from database → API → frontend.
 * 
 * This module solves the recurring Redux data structure mismatch issues by:
 * 1. Normalizing all API responses to a consistent format
 * 2. PRESERVING snake_case naming (no conversion)
 * 3. Handling all the different response envelope formats
 * 4. Providing consistent error handling
 * 
 * Usage in sagas:
 * const { data, metadata, error } = yield call(apiClient.get, '/users');
 * if (error) { ... } else { yield put({ type: 'SUCCESS', payload: data }); }
 */

import axios from 'axios';
import { getToken } from '../Utils/Common';

const apiClient = axios.create({
  baseURL: process.env.NODE_ENV === 'development' ? '' : process.env.REACT_APP_SERVER_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  // Required for device trust cookies (NIST 800-63B compliant 2FA bypass)
  withCredentials: true,
});

// Request interceptor: Add auth headers only (no case conversion needed)
apiClient.interceptors.request.use(config => {
  // Check if this is a public endpoint that should skip auth
  const skipAuth = config.headers && config.headers['X-Skip-Auth'];
  
  if (skipAuth) {
    // Remove the skip-auth header so it doesn't get sent to the server
    delete config.headers['X-Skip-Auth'];
  } else {
    // Add JWT token using the same method as existing axiosConfig
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  
  // No case conversion - frontend already uses snake_case per architecture
  return config;
});

// Response interceptor: Normalize all API responses (preserving snake_case)
apiClient.interceptors.response.use(
  response => {
    // CRITICAL: Don't process binary responses (PDFs, files, etc.)
    const responseType = response.config.responseType;
    if (responseType === 'blob' || responseType === 'arraybuffer') {
      // Return raw response for binary data
      return response;
    }
    
    // Keep original data in snake_case - no conversion per architectural decision
    const responseData = response.data;
    const config = response.config;
    
    // Normalize the different response envelope formats to a consistent structure
    let extractedData = null;
    let metadata = {};
    
    // Handle Users API: { success: true, data: { items: [...], pagination: {...} } }
    if (responseData.data?.items) {
      extractedData = responseData.data.items;
      metadata.pagination = responseData.data.pagination;
    }
    // Handle Recordings API FIRST: { status: true, data: [...], total: 0, limit: 50 }
    // Check for recordings endpoints specifically to preserve total/limit
    else if (config && config.url.includes('recordings') && responseData.data && responseData.total !== undefined) {
      extractedData = responseData.data;
      metadata.total = responseData.total;
      metadata.limit = responseData.limit || 50;
    }
    // Handle Organizations API: { status: true, data: [array] }
    else if (Array.isArray(responseData.data)) {
      extractedData = responseData.data;
    }
    // Handle other APIs with data field
    else if (responseData.data) {
      extractedData = responseData.data;
    }
    // Handle search APIs and other formats
    else {
      extractedData = responseData.data || responseData;
    }
    
    // Special handling for norms API which returns object with metric keys
    if (config && config.url.includes('norms')) {
      // For norms endpoint, preserve the original structure if it has metric keys
      if (responseData.data && typeof responseData.data === 'object' && !Array.isArray(responseData.data)) {
        extractedData = responseData.data;
      }
    }
    
    // Only wrap in array if it's clearly meant to be a list endpoint
    // Don't wrap if it's null, undefined, or already an array
    if (extractedData && !Array.isArray(extractedData) && typeof extractedData === 'object') {
      // Check if URL suggests this should be a list (search, users, recordings, etc.)
      // Exclude auth endpoints that contain 'users' but aren't list endpoints
      const isAuthEndpoint = config && (
        config.url.includes('mfa-signin') ||
        config.url.includes('verify-2fa') ||
        config.url.includes('resend-2fa') ||
        config.url.includes('signin') || 
        config.url.includes('signup') || 
        config.url.includes('login') ||
        config.url.includes('forgot') ||
        config.url.includes('reset') ||
        config.url.includes('change-password')
      );
      
      // Check if this is a single resource endpoint (e.g., /clients/123)
      const isSingleResourceEndpoint = config && config.url.match(/\/(clients|users|recordings|organizations)\/\d+$/);
      
      const isListEndpoint = config && !isAuthEndpoint && !isSingleResourceEndpoint && (
        config.url.includes('search') || 
        config.url.includes('users') || 
        config.url.includes('recordings') || 
        config.url.includes('organizations') ||
        config.url.includes('clients') ||
        config.url.includes('all-')
      ) && !config.url.includes('create') && !config.url.includes('add') && !config.url.includes('update');
      
      if (isListEndpoint) {
        extractedData = [extractedData];
      }
    }
    
    // Preserve message field in metadata if it exists
    if (responseData.message) {
      metadata.message = responseData.message;
    }
    
    return {
      data: extractedData,
      metadata: metadata,
      error: null,
      success: responseData.success || responseData.status || false,
    };
  },
  error => {
    // Normalize errors to a consistent format
    const normalizedError = {
      status: error.response?.status || 500,
      message: error.response?.data?.message || error.message || 'Network error',
      details: error.response?.data?.errors || null,
      code: error.response?.data?.code || 'UNKNOWN_ERROR',
    };
    
    // Return resolved promise with error - let sagas handle error state
    return Promise.resolve({
      data: null,
      metadata: {},
      error: normalizedError,
      success: false,
    });
  }
);

export default apiClient;
